import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface Category {
  icon: string;
  label: string;
  img: string;
  imgAlt: string;
  body: ReactNode[];
  link: string;
  linkLabel: string;
}

const CATEGORIES: Category[] = [
  {
    icon: 'ri-home-4-line',
    label: 'Cabin Rentals in Highlands, NC',
    img: 'https://public.readdy.ai/ai/img_res/1406b9b6e52b658086b157427b2d86b2.jpg',
    imgAlt: 'Cabin rentals in Highlands NC',
    body: [
      "Cabin rentals are the most popular type of Highlands NC lodging for a reason. They offer privacy, outdoor space, a full kitchen, and a genuine mountain atmosphere that hotels simply can't replicate. Most cabins in Highlands are set in wooded, secluded settings — often within a few minutes' drive of downtown — giving you the best of both worlds.",
      (<><a href="https://glenfallscabin.com" target="_blank" rel="noopener noreferrer" className="text-[#2D4A3E] underline underline-offset-2 hover:text-[#1e3329] transition-colors font-medium">Glen Falls Luxury Cabin</a> is the standout option in this category. With a 5.0-star Airbnb Guest Favorite rating, indoor and outdoor fireplaces, a covered deck, and walking distance to Glen Falls waterfall, it consistently earns the highest marks among cabin rentals in the area. Other well-regarded options include Laurelwood, Stag Ridge, and Falls Drive Cottage.</>),
    ],
    link: '/cabins-in-highlands-nc',
    linkLabel: 'View Cabin Guide',
  },
  {
    icon: 'ri-key-line',
    label: 'Vacation Rentals in Highlands, NC',
    img: 'https://readdy.ai/api/search-image?query=cozy%20mountain%20vacation%20rental%20home%20interior%20living%20room%20stone%20fireplace%20warm%20lighting%20comfortable%20furniture%20Blue%20Ridge%20Mountains%20North%20Carolina%20rustic%20luxury%20aesthetic%20editorial%20interior%20photography&width=800&height=560&seq=pts-vacation-rental-category-01&orientation=landscape',
    imgAlt: 'Vacation rentals in Highlands NC',
    body: [
      'Whole-home vacation rentals in Highlands, NC give you the full run of a private property — typically a cabin or mountain home — for the duration of your stay. This means your own kitchen, living space, outdoor area, and no shared walls with other guests. For families, groups, or anyone who values privacy, this is often the preferred option.',
      'The vacation rental market in Highlands skews toward quality. Most listings are well-maintained, thoughtfully furnished, and set in genuinely beautiful locations. Platforms like Airbnb and VRBO both have strong inventory here, and many of the best properties are booked well in advance during peak season.',
    ],
    link: '/vacation-rentals-highlands-nc',
    linkLabel: 'View Vacation Rental Guide',
  },
];

const HOTELS = [
  {
    name: 'Old Edwards Inn & Spa',
    tag: 'Premier Full-Service Hotel',
    img: 'https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/47731f199d21bda1f88f2078c66c8b6c.jpeg',
    imgAlt: 'Old Edwards Inn and Spa Highlands NC',
    description:
      'A landmark property in the heart of downtown Highlands, Old Edwards Inn and Spa is the premier full-service hotel in the area. Known for its acclaimed on-site restaurant, full spa, and refined mountain hospitality, it draws guests who want a polished, hotel-style experience steps from Main Street shops and dining.',
    features: ['Full spa & wellness center', 'Award-winning on-site dining', 'Downtown Highlands location', 'Boutique rooms & suites'],
    bestFor: 'Couples, spa retreats, special occasions',
    link: 'https://www.oldedwardsinn.com',
  },
  {
    name: 'Highlander Mountain House',
    tag: 'Boutique Mountain Hotel',
    img: 'https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/bba3421cda485e1e3cc5755692bf0a45.jpeg',
    imgAlt: 'Highlander Mountain House Highlands NC',
    description:
      'Highlander Mountain House brings a stylish mid-century modern aesthetic to the Highlands lodging scene. Set on a hillside with sweeping mountain views, it features a well-regarded restaurant, thoughtfully designed rooms, and a relaxed but elevated atmosphere. A great choice for travelers who want personality and design alongside comfort.',
    features: ['Mountain view rooms', 'On-site restaurant & bar', 'Mid-century modern design', 'Relaxed boutique atmosphere'],
    bestFor: 'Design-conscious travelers, couples, weekend getaways',
    link: 'https://www.highlandermountainhouse.com',
  },
];

export default function PTSCategorySection() {
  return (
    <section className="bg-[#FAFAF8] py-24 px-6 lg:px-14">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#C9A96E] text-xs tracking-[0.3em] uppercase font-jost mb-4">/ Lodging Types</p>
          <h2 className="font-cormorant text-5xl text-[#1A1A18] font-light">
            Types of Places to Stay<br /><em className="italic">in Highlands, NC</em>
          </h2>
        </div>

        {/* Cabin + Vacation Rental categories — alternating image/text layout */}
        <div className="space-y-16 mb-20">
          {CATEGORIES.map((cat, idx) => (
            <div
              key={cat.label}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${idx % 2 === 1 ? 'lg:flex lg:flex-row-reverse' : ''}`}
            >
              <div className="rounded-2xl overflow-hidden h-[320px]">
                <img
                  src={cat.img}
                  alt={cat.imgAlt}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EDE8E0]">
                    <i className={`${cat.icon} text-[#2D4A3E] text-base`} />
                  </div>
                  <h3 className="font-cormorant text-3xl text-[#1A1A18] font-medium">{cat.label}</h3>
                </div>
                <div className="w-10 h-px bg-[#C9A96E] mb-6" />
                {cat.body.map((para, i) => (
                  <p key={i} className="text-[#5C5850] font-jost text-sm leading-relaxed mb-4">{para}</p>
                ))}
                {cat.link && (
                  <Link
                    to={cat.link}
                    className="inline-flex items-center gap-2 border border-[#2D4A3E] text-[#2D4A3E] text-xs tracking-[0.18em] uppercase font-jost px-6 py-3 rounded-full hover:bg-[#2D4A3E] hover:text-white transition-all cursor-pointer whitespace-nowrap mt-2"
                  >
                    {cat.linkLabel}
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs" />
                    </span>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Hotels and Inns — 2-column card grid */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EDE8E0]">
              <i className="ri-building-line text-[#2D4A3E] text-base" />
            </div>
            <h3 className="font-cormorant text-3xl text-[#1A1A18] font-medium">Hotels and Inns in Highlands, NC</h3>
          </div>
          <div className="w-10 h-px bg-[#C9A96E] mb-6 ml-12" />
          <p className="text-[#5C5850] font-jost text-sm leading-relaxed mb-10 max-w-3xl">
            Highlands has a small but well-regarded hotel and inn scene. These properties are best for travelers who prefer full-service hospitality, on-site dining, and a more traditional hotel experience over the privacy of a cabin.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {HOTELS.map((hotel) => (
              <div key={hotel.name} className="bg-white rounded-2xl overflow-hidden border border-[#EDE8E0]">
                {/* Image */}
                <div className="w-full h-[240px] overflow-hidden">
                  <img
                    src={hotel.img}
                    alt={hotel.imgAlt}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Content */}
                <div className="p-7">
                  <span className="text-[#C9A96E] text-[10px] tracking-[0.25em] uppercase font-jost">{hotel.tag}</span>
                  <h4 className="font-cormorant text-2xl text-[#1A1A18] font-medium mt-1 mb-3">{hotel.name}</h4>
                  <div className="w-8 h-px bg-[#C9A96E] mb-4" />
                  <p className="text-[#5C5850] font-jost text-sm leading-relaxed mb-5">{hotel.description}</p>

                  {/* Features */}
                  <ul className="space-y-1.5 mb-5">
                    {hotel.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-[#5C5850] font-jost text-xs">
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-line text-[#2D4A3E] text-xs" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* Best for */}
                  <p className="text-[#5C5850] font-jost text-xs mb-5">
                    <span className="text-[#2D4A3E] font-medium">Best for:</span> {hotel.bestFor}
                  </p>

                  {/* CTA */}
                  <a
                    href={hotel.link}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-[#2D4A3E] text-[#2D4A3E] text-xs tracking-[0.18em] uppercase font-jost px-5 py-2.5 rounded-full hover:bg-[#2D4A3E] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                  >
                    Visit Website
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-external-link-line text-xs" />
                    </span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
